import api from './axios';

/**
 * Calendar API service for fetching appointments, availability, and unavailable blocks
 * from Cliniko via our backend API
 */

interface AppointmentParams {
  startDate?: string;
  endDate?: string;
  practitionerId?: string;
  patientId?: string;
}

interface UnavailableBlockParams {
  startDate?: string;
  endDate?: string;
  practitionerId?: string;
}

/**
 * Get appointments for a date range
 */
export const getAppointments = async (params: AppointmentParams) => {
  try {
    const response = await api.get('/api/appointments', {
      params: {
        starts_at_gteq: params.startDate,
        ends_at_lteq: params.endDate,
        practitioner_id: params.practitionerId,
        patient_id: params.patientId
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching appointments:', error);
    throw error;
  }
};

/**
 * Get unavailable blocks for practitioners
 */
export const getUnavailableBlocks = async (params: UnavailableBlockParams) => {
  try {
    const response = await api.get('/api/unavailable-blocks', {
      params: {
        starts_at_gteq: params.startDate,
        ends_at_lteq: params.endDate,
        practitioner_id: params.practitionerId
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching unavailable blocks:', error);
    throw error;
  }
};

/**
 * Get daily availability for a practitioner
 */
export const getDailyAvailability = async (practitionerId: string) => {
  try {
    const response = await api.get(`/api/practitioners/${practitionerId}/daily-availability`);
    return response.data;
  } catch (error) {
    console.error('Error fetching daily availability:', error);
    throw error;
  }
};

/**
 * Create a new unavailable block
 */
export const createUnavailableBlock = async (unavailableBlock: {
  practitionerId: string;
  startDate: string;
  endDate: string;
  notes?: string;
  unavailableBlockTypeId?: string;
}) => {
  try {
    const response = await api.post('/api/unavailable-blocks', {
      practitioner_id: unavailableBlock.practitionerId,
      starts_at: unavailableBlock.startDate,
      ends_at: unavailableBlock.endDate,
      notes: unavailableBlock.notes,
      unavailable_block_type_id: unavailableBlock.unavailableBlockTypeId
    });
    return response.data;
  } catch (error) {
    console.error('Error creating unavailable block:', error);
    throw error;
  }
};

/**
 * Update an existing unavailable block
 */
export const updateUnavailableBlock = async (
  id: string,
  unavailableBlock: {
    practitionerId?: string;
    startDate?: string;
    endDate?: string;
    notes?: string;
    unavailableBlockTypeId?: string;
  }
) => {
  try {
    const response = await api.patch(`/api/unavailable-blocks/${id}`, {
      practitioner_id: unavailableBlock.practitionerId,
      starts_at: unavailableBlock.startDate,
      ends_at: unavailableBlock.endDate,
      notes: unavailableBlock.notes,
      unavailable_block_type_id: unavailableBlock.unavailableBlockTypeId
    });
    return response.data;
  } catch (error) {
    console.error('Error updating unavailable block:', error);
    throw error;
  }
};

/**
 * Delete an unavailable block
 */
export const deleteUnavailableBlock = async (id: string) => {
  try {
    await api.delete(`/api/unavailable-blocks/${id}`);
    return true;
  } catch (error) {
    console.error('Error deleting unavailable block:', error);
    throw error;
  }
};

// Create a custom event for appointment updates
export const appointmentChangeEvent = new EventTarget();

/**
 * Create a new appointment
 */
export const createAppointment = async (appointment: {
  practitionerId: string;
  patientId: string;
  appointmentTypeId: string;
  startDate: string;
  endDate: string;
  notes?: string;
  timezone?: string;
}) => {
  try {
    const response = await api.post('/api/appointments', {
      practitioner_id: appointment.practitionerId,
      patient_id: appointment.patientId,
      appointment_type_id: appointment.appointmentTypeId,
      starts_at: appointment.startDate,
      ends_at: appointment.endDate,
      notes: appointment.notes,
      timezone: appointment.timezone
    });
    
    // Dispatch event to notify all calendar instances about the appointment change
    appointmentChangeEvent.dispatchEvent(new CustomEvent('appointment-created', { 
      detail: response.data 
    }));
    
    return response.data;
  } catch (error) {
    console.error('Error creating appointment:', error);
    throw error;
  }
};

/**
 * Update an existing appointment
 */
export const updateAppointment = async (
  id: string,
  appointment: {
    practitionerId?: string;
    patientId?: string;
    appointmentTypeId?: string;
    startDate?: string;
    endDate?: string;
    notes?: string;
    didNotArrive?: boolean;
  }
) => {
  try {
    const response = await api.patch(`/api/appointments/${id}`, {
      practitioner_id: appointment.practitionerId,
      patient_id: appointment.patientId,
      appointment_type_id: appointment.appointmentTypeId,
      starts_at: appointment.startDate,
      ends_at: appointment.endDate,
      notes: appointment.notes,
      did_not_arrive: appointment.didNotArrive
    });
    
    // Dispatch event to notify all calendar instances about the appointment change
    appointmentChangeEvent.dispatchEvent(new CustomEvent('appointment-updated', { 
      detail: response.data 
    }));
    
    return response.data;
  } catch (error) {
    console.error('Error updating appointment:', error);
    throw error;
  }
};

/**
 * Cancel an appointment
 */
export const cancelAppointment = async (
  id: string,
  cancellation: {
    cancellationReason: number;
    cancellationNote?: string;
  }
) => {
  try {
    const response = await api.post(`/api/appointments/${id}/cancel`, {
      cancellation_reason: cancellation.cancellationReason,
      cancellation_note: cancellation.cancellationNote
    });
    
    // Dispatch event to notify all calendar instances about the appointment change
    appointmentChangeEvent.dispatchEvent(new CustomEvent('appointment-cancelled', { 
      detail: response.data 
    }));
    
    return response.data;
  } catch (error) {
    console.error('Error cancelling appointment:', error);
    throw error;
  }
};

/**
 * Get patients from local database first, then fall back to Cliniko if needed
 */
export const getPatients = async (searchQuery?: string) => {
  try {
    if (!searchQuery || searchQuery.length < 2) {
      // Get all patients from local database if no search query
      const response = await api.get('/api/patients');
      return response.data;
    }

    // First, try searching in the local database
    try {
      const localResponse = await api.get('/api/patients', {
        params: { q: searchQuery }
      });
      
      // If we found patients locally, return them
      if (localResponse.data.patients && localResponse.data.patients.length > 0) {
        console.log('Found patients in local database:', localResponse.data.patients);
        return localResponse.data;
      }
    } catch (localError) {
      console.warn('Error searching local patients:', localError);
      // Continue to Cliniko search if local search fails
    }
    
    // If no local results or local search failed, try Cliniko
    const clinikoResponse = await api.get('/api/search-cliniko-patients', {
      params: { query: searchQuery }
    });
    
    console.log('Found patients in Cliniko:', clinikoResponse.data.patients);
    return clinikoResponse.data;
  } catch (error) {
    console.error('Error fetching patients:', error);
    throw error;
  }
};

/**
 * Get all appointment types from Cliniko directly
 */
export const getAppointmentTypes = async () => {
  try {
    const response = await api.get('/api/cliniko/appointment_types');
    console.log('Raw appointment types API response:', response);
    
    // Handle potential response structures
    if (response.data && response.data.appointment_types) {
      return { 
        appointment_types: response.data.appointment_types 
      };
    } else {
      console.error('Unexpected appointment types response structure:', response);
      throw new Error('Unexpected appointment types response structure');
    }
  } catch (error) {
    console.error('Error fetching appointment types:', error);
    // Return known appointment types from Cliniko for BetterMD as a fallback
    return { 
      appointment_types: [
        { id: '1194097000748949287', name: 'IV Drip Appointment' },
        { id: '1092387684841692462', name: 'Initial Consultation' },
        { id: '1147115227389631491', name: 'Follow-up Appointment' },
        { id: '1092389245760314674', name: 'IV Therapy Session' },
        { id: '1147112842961361920', name: 'Remote Consultation' }
      ]
    };
  }
};

/**
 * Get all practitioners from Cliniko directly
 */
export const getPractitioners = async () => {
  try {
    const response = await api.get('/api/cliniko/practitioners');
    return { 
      practitioners: response.data.practitioners || [] 
    };
  } catch (error) {
    console.error('Error fetching practitioners:', error);
    // Return mock data in case of error
    return { 
      practitioners: [
        { id: '1', first_name: 'Dr.', last_name: 'Smith', title: 'MD' },
        { id: '2', first_name: 'Dr.', last_name: 'Johnson', title: 'DO' },
        { id: '3', first_name: 'Jane', last_name: 'Davis', title: 'NP' }
      ]
    };
  }
};

/**
 * Get unavailable block types from Cliniko
 */
export const getUnavailableBlockTypes = async () => {
  try {
    const response = await api.get('/api/unavailable-block-types');
    return response.data;
  } catch (error) {
    console.error('Error fetching unavailable block types:', error);
    // Return mock data in case of error
    return { 
      unavailable_block_types: [
        { id: '1', name: 'Lunch', color: '#f44336', duration_in_minutes: 60 },
        { id: '2', name: 'Meeting', color: '#ff9800', duration_in_minutes: 30 },
        { id: '3', name: 'Out of Office', color: '#9c27b0', duration_in_minutes: 240 }
      ]
    };
  }
};

/**
 * Get treatment note templates from Cliniko
 */
export const getTreatmentNoteTemplates = async () => {
  try {
    const response = await api.get('/api/treatment-note-templates', {
      params: {
        sort: 'updated_at:desc',
        archived: 'false'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching treatment note templates:', error);
    throw error;
  }
};

/**
 * Get a specific treatment note template by ID
 */
export const getTreatmentNoteTemplate = async (templateId: string) => {
  try {
    const response = await api.get(`/api/treatment-note-templates/${templateId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching treatment note template ${templateId}:`, error);
    throw error;
  }
};